<template>
	<div class="follow-up-editor">
		<div class="modal-body">
			<div class="alert alert-warning mb-4" v-if="showWarning">
				Please be aware, that this settings will override all follow up text settings on the selected products.
			</div>
			<ul class="nav nav-tabs nav-fill" role="tablist">
				<li
					class="nav-item"
					role="tab"
					v-for="category in filteredCategories"
					:key="category.key"
					@click="selectedCategory = category">
					<a class="nav-link" :class="{ 'active': category.key === selectedCategory.key  }">{{category.label}}</a>
				</li>
			</ul>
			<div class="tab-content">
				<auto-text-editor
					:category="selectedCategory.key"
					:description="selectedCategory.description"
					:sms-available="!!autotextsSms[selectedCategory.key]"
					:sms-integrated="isHasSmsGateway"
					:content="defaultWaContent"
					:default-text="defaultSmsContent"
					:smsGateways="smsGateways"
					:smsGatewayOptions="smsGatewayOptions"
					:waGatewayOptions="waGatewayOptions"
					v-bind:waGateways.sync="waGateways"
					:user="user"
					@updateWaGateways="updateWaGateways"
					@saveProductSmsGateways="addSmsGateways"
					v-bind:autotext.sync="autotexts[selectedCategory.key]"
					v-bind.sync="autotextsSms[selectedCategory.key]"
					v-model="autotexts[selectedCategory.key]"
					v-if="!isFetching">
				</auto-text-editor>
				<loader v-else></loader>
			</div>
		</div>
		<div class="modal-footer">
			<button type="button" class="btn btn-light" @click="$emit('cancel')">{{ $t('Close') }}</button>
			<button type="button" class="btn btn-primary" :class="{ 'is-loading': isSaving }" @click="save">{{ $t('Save') }}</button>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Loader from './Loader.vue';
import AutoTextEditor from './AutoTextEditor.vue';
import {
	followUps,
	defaultFollowUp,
	defaultFollowUpSms,
} from '../lib/statics';
import { duplicateVar, groupBy } from '../lib/helper';
import productsApi from '../api/products';

export default {
	name: 'ProductFollowUp',
	props: {
		productIds: {
			type: Array,
			default: () => [],
		},
		addonId: {
			type: String,
		},
		showWarning: {
			type: Boolean,
			default: true,
		},
		hiddenTabs: {
			type: Array,
			default: () => [],
		},
		followUp: {
			type: Object,
		},
	},
	components: {
		Loader,
		AutoTextEditor,
	},
	data() {
		const categories = duplicateVar(followUps);
		const selectedCategory = categories[0];
		return {
			categories,
			selectedCategory,
			autotexts: defaultFollowUp,
			autotextsSms: defaultFollowUpSms,
			defaultWaContent: duplicateVar(defaultFollowUp),
			defaultSmsContent: duplicateVar(defaultFollowUpSms),
			waGateways: [],
			smsGateways: [],
			isSaving: false,
		};
	},
	methods: {
		...mapActions({
			fetchIntegrations: 'settings/fetchAccounts',
		}),
		updateWaGateways(waGateways) {
			this.waGateways = waGateways;
		},
		addSmsGateways(smsGateway) {
			this.smsGateways.push(smsGateway);
		},
		save() {
			this.isSaving = true;
			if (this.addonId) {
				const params = {
					wa_gateways: this.waGateways,
					sms_gateways: this.smsGateways,
					follow_up_autotext: this.autotexts,
					sms_autotext: this.autotextsSms,
				};
				this.$emit('save', params);
				this.isSaving = false;
			} else {
				const waGateways = JSON.stringify(this.waGateways);
				const smsGateways = JSON.stringify(this.smsGateways);
				const autotexts = JSON.stringify(this.autotexts);
				const autotextsSms = JSON.stringify(this.autotextsSms);
				const params = {
					// product_ids: productIds,
					wa_gateways: waGateways,
					sms_gateways: smsGateways,
					follow_up_autotext: autotexts,
					sms_autotext: autotextsSms,
				};
				// Product IDs
				if (this.productIds.length > 0) {
					const productIds = JSON.stringify(this.productIds);
					params.product_ids = productIds;
				}
				const callback = () => {
					this.isSaving = false;
					this.$notify({
						group: 'app',
						title: 'Follow-up Text',
						text: 'Successfully Updated',
						type: 'success',
					});
				};
				const errorCallback = () => {
					this.isSaving = false;
				};
				productsApi.updateAutoTextBulk(params, callback, errorCallback);
			}
		},
		initData() {
			if (this.followUp) {
				const waGateways = this.followUp.waGateways;
				const smsGateways = this.followUp.smsGateways;
				const waAutotext = this.followUp.waAutotext;
				const smsAutotext = this.followUp.smsAutotext;

				this.waGateways = waGateways;
				this.smsGateways = smsGateways;
				this.autotexts = waAutotext;
				this.autotextsSms = smsAutotext;
			}
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			integrations: 'settings/accounts',
			isFetching: 'settings/isFetching',
		}),
		waGatewayOptions() {
			const integrations = this.integrations.filter(({ type }) => type === 'wa_gateway');
			const waGatewayOptions = integrations.map((waGateway) => {
				const gateway = {
					name: `${waGateway.name} - ${waGateway.account_name}`,
					value: waGateway.name,
				};
				return gateway;
			});
			const groupedOptions = groupBy(waGatewayOptions, 'value');
			const optionValues = Object.keys(groupedOptions);
			const options = optionValues.map(value => ({ value }));
			return options;
		},
		smsGatewayOptions() {
			const integrations = this.integrations.filter(({ type }) => type === 'sms_gateway');
			const smsGateways = integrations.reduce((options, item) => {
				const smsGateway = {
					name: `${item.name} - ${item.account_name}`,
					value: item.name,
				};
				options.push(smsGateway);
				return options;
			}, []);
			return smsGateways;
		},
		isHasSmsGateway() {
			return this.smsGatewayOptions.length > 0;
		},
		filteredCategories() {
			return this.categories.filter(({ key }) => !this.hiddenTabs.includes(key));
		},
	},
	created() {
		this.initData();
		this.fetchIntegrations();
	},
};
</script>
